import React, { useState } from "react";
import { useGame, usePlayer } from "@empirica/core/player/classic/react";
import {
  contributionEstimate,
  getContributionAssessment,
  saveContributionAssessment,
} from "../../../shared/contributionAssessment.js";
import { Button } from "../components/Button";
import { DonutChart } from "../negotiation/NegotiationDonutCharts";
import { dollars } from "../formatMoney";
import { getCounterpartCopy, isLossFrameDesign } from "../payoffFrame";

export function Stage2ContributionQuestion({ timing, next, disabled = false }) {
  const game = useGame();
  const player = usePlayer();
  const saved = getContributionAssessment(player, timing);
  const draftKey = `stage2ContributionSliderDraft_${timing}`;
  const draft = player.get(draftKey) ?? (player.get("stage2ContributionTiming") === timing
    ? player.get("stage2ContributionSliderDraft")
    : undefined);
  const [sliderValue, setSliderValue] = useState(() =>
    saved?.sliderPercent ?? saved?.percent ?? draft ?? 50
  );
  const [sliderTouched, setSliderTouched] = useState(() =>
    Boolean(saved) || draft != null
  );
  const total = Number(game.get("stage2")?.teamWinningsBeforeAdjustment);
  const displayedSliderValue = total === 0 ? 0 : sliderValue;
  const lossFrame = isLossFrameDesign(game.get("design"));
  const counterpart = getCounterpartCopy(game.get("design"));
  const estimate = contributionEstimate((total * sliderValue / 100).toFixed(2), total);
  const locked = disabled || Boolean(saved);
  const canSubmit = Boolean(estimate) && (sliderTouched || total === 0) && !locked;

  function updateSlider(value) {
    if (locked || total === 0) return;
    setSliderValue(value);
    setSliderTouched(true);
    player.set(draftKey, value);
  }

  function handleSubmit(event) {
    event.preventDefault();
    if (!canSubmit) return;
    saveContributionAssessment(player, {
      ...estimate,
      sliderPercent: total > 0 ? sliderValue : null,
      timing,
      submittedAt: new Date().toISOString(),
    });
    next();
  }

  return (
    <div className="w-full max-w-[60rem] mx-auto px-8 py-6 space-y-6">
      <h2 className="text-4xl font-semibold text-gray-900">Your contribution to Game 2</h2>
      <p className="text-2xl text-gray-700">
        In Game 2, you and your {counterpart.term} {lossFrame ? "saved" : "earned"}{" "}
        <strong>{dollars(total)}</strong> in total.
      </p>
      <form className="space-y-6" onSubmit={handleSubmit}>
        <div className="space-y-3">
          <label htmlFor="game2-contribution" className="block text-2xl font-medium text-gray-900">
            What portion of the winnings do you think you contributed?
          </label>
          <p id="game2-contribution-help" className="text-xl text-gray-600">
            Move or click the slider to select your contribution.
          </p>
          <div className="flex items-center gap-4 pt-10 pb-4">
            <span className="text-2xl font-medium">0%</span>
            <div className="relative flex-1 min-w-0">
              <span
                aria-hidden="true"
                className="pointer-events-none absolute whitespace-nowrap text-2xl font-semibold tabular-nums text-gray-900"
                style={{
                  // The 18px thumb travels within the input's width.
                  left: `calc(${displayedSliderValue}% + ${9 - displayedSliderValue * 0.18}px)`,
                  bottom: "calc(100% + 10px)",
                  transform: "translateX(-50%)",
                }}
              >
                {displayedSliderValue}%
              </span>
              <input
                id="game2-contribution"
                type="range"
                min={0}
                max={100}
                step={1}
                autoFocus
                value={displayedSliderValue}
                disabled={locked || total === 0}
                aria-describedby="game2-contribution-help game2-contribution-preview"
                aria-valuetext={total === 0 ? "$0.00 of $0.00" : `${sliderValue}%: ${dollars(estimate?.amountDollars)} of ${dollars(total)}`}
                className="block filled-slider focus-visible:ring-2 focus-visible:ring-empirica-500 focus-visible:ring-offset-4"
                style={{ "--val": displayedSliderValue, minWidth: 0, margin: 0 }}
                onChange={(event) => updateSlider(Number(event.target.value))}
                onPointerUp={(event) => updateSlider(Number(event.currentTarget.value))}
                onKeyDown={(event) => {
                  if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) {
                    updateSlider(Number(event.currentTarget.value));
                  }
                }}
              />
            </div>
            <span className="text-2xl font-medium">100%</span>
          </div>
        </div>
        <div id="game2-contribution-preview" aria-live="polite">
          {estimate ? (
            <DonutChart
              title="Your estimated contributions"
              subtitle={estimate.percent === null
                ? "There were no Game 2 winnings to contribute to."
                : `Your claimed contribution: ${sliderValue}% of the total.`}
              centerValue={estimate.amountDollars}
              centerTotal={estimate.totalDollars}
              centerValueClassName="fill-indigo-600 text-base font-semibold"
              centerTotalClassName="fill-gray-900 text-base font-semibold"
              responsive
              showZeroValueLegendItems
              legendTextClassName="text-2xl"
              stableLegendValues
              segments={[
                { label: "Your claimed contribution", value: estimate.amountDollars, color: "#4f46e5", percentLabel: `${total > 0 ? sliderValue : 0}%` },
                { label: `${counterpart.capitalizedTerm}'s implied contribution`, value: estimate.partnerAmountDollars, color: "#dc2626", percentLabel: `${total > 0 ? 100 - sliderValue : 0}%` },
              ]}
            />
          ) : (
            <p className="rounded-md border border-gray-200 p-5 text-xl text-gray-600">
              Loading Game 2 winnings…
            </p>
          )}
        </div>
        <Button type="submit" disabled={!canSubmit} className="text-xl">Continue</Button>
        {locked ? <p role="status" className="text-lg text-gray-500">Response saved. Waiting for {counterpart.term}...</p> : null}
      </form>
    </div>
  );
}
